import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield, 
  TrendingUp, 
  BookOpen, 
  DollarSign, 
  ArrowRight, 
  CheckCircle,
  Star,
  Mail,
  Lock,
  Users,
  BarChart3,
  Globe,
  Twitter,
  Linkedin,
  Github
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Sample Bitcoin price data for the chart
const bitcoinData = [
  { time: 'Jan', price: 42000 },
  { time: 'Feb', price: 45000 },
  { time: 'Mar', price: 38000 },
  { time: 'Apr', price: 41000 },
  { time: 'May', price: 47000 },
  { time: 'Jun', price: 52000 },
  { time: 'Jul', price: 48000 },
  { time: 'Aug', price: 55000 },
  { time: 'Sep', price: 58000 },
  { time: 'Oct', price: 62000 },
  { time: 'Nov', price: 65000 },
  { time: 'Dec', price: 68000 },
];

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.5 }
};

function App() {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubmitted(true);
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
      }, 3000);
    }
  };

  const scrollToSignup = () => {
    document.getElementById('signup-section').scrollIntoView({ 
      behavior: 'smooth' 
    });
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-dark-bg/90 backdrop-blur-sm border-b border-dark-border z-50">
        <div className="container-custom">
          <div className="flex justify-between items-center py-4">
            <motion.div 
              className="text-2xl font-bold text-gradient"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              CryptoInvest
            </motion.div>
            <motion.button 
              className="btn-primary"
              onClick={scrollToSignup}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Comenzar
            </motion.button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 section-padding">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <motion.h1 
              className="text-5xl md:text-7xl font-bold mb-6"
              variants={fadeInUp}
            >
              Aprende con nosotros –{' '}
              <span className="text-gradient">Inversión Cripto</span>{' '}
              Simple y Segura
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto"
              variants={fadeInUp}
            >
              Tu dinero, tu control – criptomonedas hechas simples. Comienza tu viaje hacia la libertad financiera con nuestra plataforma amigable para principiantes.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              variants={fadeInUp}
            >
              <button 
                className="btn-primary text-lg px-10 py-5"
                onClick={scrollToSignup}
              >
                Comenzar <ArrowRight className="inline ml-2" size={20} />
              </button>
              <button className="btn-secondary text-lg px-10 py-5">
                Saber Más
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-dark-card/30">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Por qué elegir <span className="text-gradient">CryptoInvest</span>?
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Construido específicamente para principiantes que quieren invertir de forma segura y confiada en criptomonedas.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                icon: <Shield className="w-12 h-12 text-electric-blue" />,
                title: "Seguro y Transparente",
                description: "Seguridad de nivel bancario con total transparencia. Tus fondos están protegidos con encriptación líder en la industria."
              },
              {
                icon: <TrendingUp className="w-12 h-12 text-electric-green" />,
                title: "Sin Comisiones Ocultas",
                description: "Comisiones bajas y transparentes. Sin cargos sorpresa. Mantén más de tus ganancias con nuestros precios competitivos."
              },
              {
                icon: <BookOpen className="w-12 h-12 text-electric-blue" />,
                title: "Plataforma para Principiantes",
                description: "Aprende mientras inviertes con nuestros recursos educativos e interfaz intuitiva diseñada para principiantes."
              },
              {
                icon: <Users className="w-12 h-12 text-electric-green" />,
                title: "Soporte 24/7",
                description: "Obtén ayuda cuando la necesites. Nuestro equipo de soporte experto está disponible las 24 horas para asistirte."
              }
            ].map((benefit, index) => (
              <motion.div 
                key={index}
                className="card text-center group"
                variants={scaleIn}
                whileHover={{ y: -5 }}
              >
                <div className="mb-4 flex justify-center">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Cómo <span className="text-gradient">Funciona</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Comienza a invertir en criptomonedas en solo tres simples pasos.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                step: "01",
                title: "Regístrate",
                description: "Crea tu cuenta en minutos con solo tu email e información básica. No se requiere verificación compleja.",
                icon: <Mail className="w-8 h-8" />
              },
              {
                step: "02", 
                title: "Deposita",
                description: "Agrega fondos de forma segura usando tu cuenta bancaria o tarjeta de crédito. Comienza con tan solo $10.",
                icon: <DollarSign className="w-8 h-8" />
              },
              {
                step: "03",
                title: "Invierte",
                description: "Elige de nuestra selección curada de criptomonedas y comienza a construir tu portafolio hoy mismo.",
                icon: <TrendingUp className="w-8 h-8" />
              }
            ].map((step, index) => (
              <motion.div 
                key={index}
                className="text-center relative"
                variants={fadeInUp}
              >
                <div className="card">
                  <div className="text-6xl font-bold text-gradient mb-4">{step.step}</div>
                  <div className="flex justify-center mb-4 text-electric-blue">
                    {step.icon}
                  </div>
                  <h3 className="text-2xl font-semibold mb-4">{step.title}</h3>
                  <p className="text-gray-300">{step.description}</p>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-electric-blue" />
                  </div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Interactive Charts Section */}
      <section className="section-padding bg-dark-card/30">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Rastrea tus <span className="text-gradient">Inversiones</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Monitorea precios de criptomonedas en tiempo real y toma decisiones de inversión informadas.
            </p>
          </motion.div>

          <motion.div 
            className="max-w-6xl mx-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold">Bitcoin (BTC)</h3>
                  <p className="text-gray-300">Rendimiento últimos 12 meses</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-electric-green">$68,000</div>
                  <div className="text-sm text-electric-green">+12.5% este mes</div>
                </div>
              </div>
              
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={bitcoinData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                    <XAxis 
                      dataKey="time" 
                      stroke="#666"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#666"
                      fontSize={12}
                      tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#1A1A1A',
                        border: '1px solid #2A2A2A',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                      formatter={(value) => [`$${value.toLocaleString()}`, 'Precio']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke="#00D4FF" 
                      strokeWidth={3}
                      dot={{ fill: '#00D4FF', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#00D4FF', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Lo que dicen nuestros <span className="text-gradient">Usuarios</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Únete a miles de inversionistas satisfechos que comenzaron su viaje cripto con nosotros.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                name: "María González",
                role: "Gerente de Marketing",
                content: "CryptoInvest hizo muy fácil comenzar a invertir. Los recursos educativos me ayudaron a entender todo antes de poner mi primer euro.",
                rating: 5
              },
              {
                name: "Carlos Mendoza",
                role: "Ingeniero de Software", 
                content: "Era escéptico sobre las criptomonedas, pero las características de seguridad de la plataforma y las comisiones transparentes me convencieron. ¡Excelente experiencia hasta ahora!",
                rating: 5
              },
              {
                name: "Ana Rodríguez",
                role: "Profesora",
                content: "Como principiante completa, aprecié la guía paso a paso. El equipo de soporte fue increíblemente útil cuando tuve preguntas.",
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div 
                key={index}
                className="card"
                variants={scaleIn}
                whileHover={{ y: -5 }}
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.content}"</p>
                <div>
                  <div className="font-semibold">{testimonial.name}</div>
                  <div className="text-sm text-gray-400">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Educational Resources Section */}
      <section className="section-padding bg-dark-card/30">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Aprende antes de <span className="text-gradient">Invertir</span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Accede a nuestros recursos educativos completos diseñados específicamente para principiantes en criptomonedas.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                {
                  title: "Guía de Conceptos Básicos",
                  description: "Todo lo que necesitas saber sobre los fundamentos de las criptomonedas"
                },
                {
                  title: "Estrategias de Inversión", 
                  description: "Aprende estrategias probadas para construir un portafolio cripto diversificado"
                },
                {
                  title: "Mejores Prácticas de Seguridad",
                  description: "Mantén tus inversiones seguras con nuestras pautas de seguridad"
                }
              ].map((resource, index) => (
                <motion.div 
                  key={index}
                  className="card text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <BookOpen className="w-12 h-12 text-electric-blue mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{resource.title}</h3>
                  <p className="text-gray-300">{resource.description}</p>
                </motion.div>
              ))}
            </div>
            
            <motion.button 
              className="btn-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Explorar Centro de Aprendizaje
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="signup-section" className="section-padding">
        <div className="container-custom">
          <motion.div 
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Listo para comenzar tu <span className="text-gradient">Viaje Cripto</span>?
            </h2>
            <p className="text-xl text-gray-300 mb-12">
              Únete a miles de inversionistas que confían en CryptoInvest para sus inversiones en criptomonedas.
            </p>
            
            <motion.div 
              className="max-w-md mx-auto"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Ingresa tu dirección de email"
                    className="flex-1 px-6 py-4 bg-dark-card border border-dark-border rounded-lg focus:border-electric-blue focus:outline-none transition-colors"
                    required
                  />
                  <motion.button 
                    type="submit"
                    className="btn-primary whitespace-nowrap"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {isSubmitted ? (
                      <>
                        <CheckCircle className="inline mr-2" size={20} />
                        ¡Suscrito!
                      </>
                    ) : (
                      'Comenzar'
                    )}
                  </motion.button>
                </div>
                {isSubmitted && (
                  <motion.p 
                    className="text-electric-green text-sm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    ¡Gracias! Te enviaremos un email de bienvenida en breve.
                  </motion.p>
                )}
              </form>
            </motion.div>
            
            <motion.div 
              className="mt-8 flex items-center justify-center space-x-8 text-sm text-gray-400"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                Seguro y Encriptado
              </div>
              <div className="flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Sin Spam, Nunca
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark-card border-t border-dark-border">
        <div className="container-custom section-padding">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold text-gradient mb-4">CryptoInvest</h3>
              <p className="text-gray-300 mb-6 max-w-md">
                Tu socio de confianza para inversiones en criptomonedas simples y seguras. 
                Comienza tu viaje hacia la libertad financiera hoy.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-electric-blue transition-colors">
                  <Twitter className="w-6 h-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-electric-blue transition-colors">
                  <Linkedin className="w-6 h-6" />
                </a>
                <a href="#" className="text-gray-400 hover:text-electric-blue transition-colors">
                  <Github className="w-6 h-6" />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-electric-blue transition-colors">Política de Privacidad</a></li>
                <li><a href="#" className="hover:text-electric-blue transition-colors">Términos de Servicio</a></li>
                <li><a href="#" className="hover:text-electric-blue transition-colors">Política de Cookies</a></li>
                <li><a href="#" className="hover:text-electric-blue transition-colors">Divulgación de Riesgos</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Soporte</h4>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#" className="hover:text-electric-blue transition-colors">Centro de Ayuda</a></li>
                <li><a href="#" className="hover:text-electric-blue transition-colors">Contáctanos</a></li>
                <li><a href="#" className="hover:text-electric-blue transition-colors">Estado del Servicio</a></li>
                <li><a href="#" className="hover:text-electric-blue transition-colors">Documentación API</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-dark-border mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 CryptoInvest. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
